import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@workspace/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { Textarea } from '@workspace/ui/components/textarea';
import type { City, Country, State } from 'country-state-city';
import { ArrowRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { m } from '~/paraglide/messages.js';
import frDepartementsJson from '../-data/FR/departements.json';
import {
  type BusinessAddressData,
  BusinessAddressSchema,
} from '../-schemas/org-schema';

async function loadCountryStateCityLib() {
  const { City, State, Country } = await import('country-state-city');
  return { City, State, Country };
}

type Props = {
  values: BusinessAddressData;
  onUpdate: (data: BusinessAddressData) => void;
  onNext: () => void;
  businessType?: 'from-scratch' | 'existing';
};

export function BusinessAddressComponent({
  values,
  onUpdate,
  onNext,
  businessType,
}: Props) {
  const [cscLib, setCscLib] = useState<{
    City: typeof City;
    State: typeof State;
    Country: typeof Country;
  } | null>(null);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>(
    [],
  );
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');

  const form = useForm<BusinessAddressData>({
    resolver: zodResolver(BusinessAddressSchema),
    defaultValues: values,
  });

  useEffect(() => {
    loadCountryStateCityLib().then((lib) => {
      setCscLib(lib);
      const allCountries = lib.Country.getAllCountries().map((country) => ({
        code: country.isoCode,
        name: country.name,
      }));
      setCountries(allCountries);
    });
  }, []);

  const country = form.watch('country');

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  const getStatesByCountry = useCallback(
    (countryCode: string) => {
      if (countryCode === 'FR') {
        return frDepartementsJson.map((dept) => ({
          name: dept.name,
          isoCode: dept.isoCode,
          countryCode: 'FR',
        }));
      }
      return cscLib?.State.getStatesOfCountry(countryCode) || [];
    },
    [cscLib],
  );

  const statesByCountry = getStatesByCountry(country);

  const filteredStates = statesByCountry.filter((s) =>
    s.name.toLowerCase().includes(stateSearch.toLowerCase()),
  );

  function onSubmit(data: BusinessAddressData) {
    onUpdate(data);
    onNext();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-4 border rounded-xl p-8">
          <FormField
            control={form.control}
            name="legalName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0">
                  {m['organization.business_address.legal_name_label']()}
                  <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={m[
                      'organization.business_address.company_name_placeholder'
                    ]()}
                    type="text"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {businessType === 'existing' && (
            <FormField
              control={form.control}
              name="taxId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0">
                    {m['organization.business_address.tax_id_label']()}
                    <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={m[
                        'organization.business_address.tax_id_placeholder'
                      ]()}
                      type="text"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="whatYourCompanyDoes"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0">
                  {m['organization.business_address.what_company_does_label']()}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={m[
                      'organization.business_address.type_placeholder'
                    ]()}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0">
                  {m['organization.business_address.company_location_label']()}
                  <span className="text-red-500">*</span>
                </FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    setCountrySearch('');
                  }}
                  defaultValue={field.value}
                  onOpenChange={(open) => {
                    if (!open) setCountrySearch('');
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={m[
                          'organization.business_address.country_placeholder'
                        ]()}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <div className="px-2 pb-2">
                      <Input
                        placeholder={m[
                          'organization.business_address.country_search_placeholder'
                        ]()}
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="h-9"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {filteredCountries.length === 0 ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          {m[
                            'organization.business_address.no_countries_found'
                          ]()}
                        </div>
                      ) : (
                        filteredCountries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))
                      )}
                    </div>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address1"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0">
                  {m['organization.business_address.address_label']()}
                  <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="address1"
                    type="text"
                    placeholder={m[
                      'organization.business_address.address_placeholder'
                    ]()}
                    className="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <div className="flex-1">
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="gap-0">
                      {m['organization.business_address.state_label']()}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setStateSearch('');
                      }}
                      defaultValue={field.value}
                      onOpenChange={(open) => {
                        if (!open) setStateSearch('');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={m[
                              'organization.business_address.state_placeholder'
                            ]()}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <div className="px-2 pb-2">
                          <Input
                            placeholder={m[
                              'organization.business_address.state_search_placeholder'
                            ]()}
                            value={stateSearch}
                            onChange={(e) => setStateSearch(e.target.value)}
                            className="h-9"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                          {filteredStates.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                              {m[
                                'organization.business_address.no_states_found'
                              ]()}
                            </div>
                          ) : (
                            filteredStates.map((state) => (
                              <SelectItem
                                key={state.isoCode}
                                value={state.isoCode}
                              >
                                {state.name}
                              </SelectItem>
                            ))
                          )}
                        </div>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex-1">
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="gap-0">
                      {m['organization.business_address.postal_code_label']()}
                      <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        id="postalCode"
                        type="text"
                        placeholder={m[
                          'organization.business_address.zip_placeholder'
                        ]()}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-4">
          <Button
            type="submit"
            className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-6 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
          >
            {m['common.continue']()}
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
